'use client'

import Image from 'next/image'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { LuCreditCard } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { toast } from '@/core/lib/toast'
import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import { useSheetNav } from '@/features/veluvit/components/overlay/sheet-nav'
import BillingMethodsPanel from '@/features/veluvit/components/wallet/billing-methods'
import PaymentOptionButton from '@/features/veluvit/components/wallet/payment-option'
import {
  chargeArt,
  chargeRewards,
} from '@/features/veluvit/lib/charge-products'
import { readBillingMethods } from '@/features/veluvit/services/billing.service'
import { runPaymentLayer } from '@/features/veluvit/services/payment-layer'
import { paymentModel } from '@/features/veluvit/services/payment-popup'
import { rememberPaymentReturn } from '@/features/veluvit/services/payment-return'
import {
  confirmPaymentOrder,
  createPaymentOrder,
  preparePurchase,
  readPaymentOrder,
  readPaymentProducts,
  startPaymentCheckout,
} from '@/features/veluvit/services/payment.service'
import { closeLayer } from '@/features/veluvit/stores/overlay.store'
import { usePaymentFlowStore } from '@/features/veluvit/stores/payment-flow.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { FormEvent } from 'react'
import type { BillingMethods } from '@/features/veluvit/schemas/billing.schema'
import type {
  PaymentModel,
  PaymentOrder,
  PaymentProduct,
  PaymentProducts,
} from '@/features/veluvit/schemas/payment.schema'

type Props = {
  ownerId: string
  product?: PaymentProduct
  order?: PaymentOrder
  catalog?: PaymentProducts
  illustrationTier?: number
}

const subscribeNever = () => () => {}
const noPaymentModel = () => null

export default function PaymentCheckoutForm({
  ownerId,
  product,
  order,
  catalog,
  illustrationTier,
}: Props) {
  const [currentOrder, setCurrentOrder] = useState(order ?? null)
  const [data, setData] = useState(catalog ?? null)
  const [cards, setCards] = useState<BillingMethods | null>(null)
  // 기기 정보라 렌더 동안 바뀌지 않는다. 서버와 첫 hydration은 null로 맞춘다
  const model = useSyncExternalStore<PaymentModel | null>(
    subscribeNever,
    paymentModel,
    noPaymentModel
  )
  const [optionId, setOptionId] = useState('')
  const [methodId, setMethodId] = useState('')
  const [pending, setPending] = useState(false)
  const [manageCards, setManageCards] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const sectionRef = useRef<HTMLElement | null>(null)
  const layerId = useRef<string | null>(null)
  const mounted = useRef(false)
  const locked = useRef(false)
  const paymentController = useRef<AbortController | null>(null)
  const prepared = useRef(false)
  const touched = useRef(false)
  const authorization = useRef<unknown>(null)
  const nav = useSheetNav()
  const screenDepth = useRef(nav?.depth)
  const completed = usePaymentFlowStore((state) => state.completed)
  const user = useSessionStore((state) => state.data?.user)
  const shownProduct = currentOrder?.product ?? product
  const selected = data?.paymentOptions.find((option) => option.id === optionId)
  const registered =
    cards?.methods.filter((item) => item.status === 'active') ?? []
  const brandpay = data?.paymentOptions.find(
    (option) => option.kind === 'brandpay'
  )
  const ready = data?.providerConfigured && data.provider === 'toss'
  const canPay =
    !!ready &&
    !!model &&
    !!selected?.available &&
    selected.models.includes(model) &&
    (selected.kind !== 'brandpay' ||
      registered.some((card) => card.id === methodId))

  useEffect(() => {
    mounted.current = true
    layerId.current =
      sectionRef.current?.closest<HTMLElement>('[data-layer-id]')?.dataset
        .layerId ?? null
    const controller = new AbortController()
    const identity = useSessionStore.getState().identity
    if (!catalog)
      void readPaymentProducts(controller.signal)
        .then((next) => {
          if (
            !controller.signal.aborted &&
            useSessionStore.getState().identity === identity
          )
            setData(next)
        })
        .catch(() => {
          if (!controller.signal.aborted)
            toast.error('결제수단을 불러오지 못했어요.')
        })
    const unsubscribe = useSessionStore.subscribe((state) => {
      if (
        state.identity !== identity ||
        state.data?.user?.id !== ownerId ||
        state.data.user.guest
      )
        paymentController.current?.abort()
    })
    return () => {
      mounted.current = false
      authorization.current = null
      unsubscribe()
      controller.abort()
      paymentController.current?.abort()
    }
  }, [catalog, ownerId])

  const navDepth = nav?.depth
  useEffect(() => {
    if (navDepth !== undefined && navDepth !== screenDepth.current) return
    const controller = new AbortController()
    void readBillingMethods(ownerId, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return
        setCards(next)
        setMethodId((current) =>
          next.methods.some(
            (card) => card.id === current && card.status === 'active'
          )
            ? current
            : (next.methods.find((card) => card.status === 'active')?.id ?? '')
        )
      })
      .catch(() => {
        if (!controller.signal.aborted) setCards(null)
      })
    return () => controller.abort()
  }, [navDepth, ownerId, manageCards])

  const [optionBasis, setOptionBasis] = useState<{
    data: PaymentProducts
    model: PaymentModel
  } | null>(null)
  if (
    data &&
    model &&
    (optionBasis?.data !== data || optionBasis.model !== model)
  ) {
    setOptionBasis({ data, model })
    setOptionId(
      data.paymentOptions.some(
        (option) =>
          option.id === optionId &&
          option.available &&
          option.models.includes(model)
      )
        ? optionId
        : (data.paymentOptions.find(
            (option) =>
              option.kind === 'payment' &&
              option.available &&
              option.models.includes(model)
          )?.id ?? '')
    )
  }

  useEffect(() => {
    const first = cards?.methods.find((card) => card.status === 'active')
    if (
      !locked.current &&
      !touched.current &&
      first &&
      model &&
      brandpay?.available &&
      brandpay.models.includes(model)
    ) {
      setMethodId(first.id)
      setOptionId('brandpay')
    }
  }, [cards, brandpay, model])

  useEffect(() => {
    if (
      completed?.ownerId === ownerId &&
      completed.orderId === currentOrder?.id &&
      mounted.current &&
      layerId.current &&
      completed.layerId === layerId.current
    ) {
      closeLayer(layerId.current)
      usePaymentFlowStore.getState().clearCompleted(ownerId, completed.orderId)
    }
  }, [completed, currentOrder?.id, ownerId])

  function openCards() {
    touched.current = false
    if (nav)
      nav.push({
        key: 'checkout-cards',
        render: () => (
          <>
            <SheetHead title="카드 등록·관리" />
            <BillingMethodsPanel ownerId={ownerId} />
          </>
        ),
      })
    else setManageCards(true)
  }

  async function check() {
    if (!currentOrder || locked.current) return
    locked.current = true
    setPending(true)
    try {
      const next = authorization.current
        ? await confirmPaymentOrder(
            ownerId,
            currentOrder,
            authorization.current
          )
        : await readPaymentOrder(ownerId, currentOrder.id)
      if (mounted.current) {
        setCurrentOrder(next)
        setConfirming(
          next.status === 'pending' && authorization.current !== null
        )
      }
      usePaymentFlowStore
        .getState()
        .watch(ownerId, next.id, layerId.current ?? undefined)
      if (next.status === 'pending')
        toast.show('결제 결과를 확인하고 있어요. 확인되면 알려드릴게요.')
    } catch {
      toast.show('주문 상태를 확인하지 못했어요. 잠시 후 다시 확인해 주세요.')
    } finally {
      locked.current = false
      if (mounted.current) setPending(false)
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (
      locked.current ||
      !shownProduct ||
      !model ||
      !canPay ||
      user?.id !== ownerId ||
      user.guest
    )
      return
    if (confirming || currentOrder?.payment.phase === 'confirming') {
      void check()
      return
    }
    locked.current = true
    setPending(true)
    const identity = useSessionStore.getState().identity
    const controller = new AbortController()
    paymentController.current = controller
    const sameOwner = () =>
      useSessionStore.getState().identity === identity &&
      useSessionStore.getState().data?.user?.id === ownerId
    const active = () =>
      mounted.current && !controller.signal.aborted && sameOwner()
    let selectedOrder = currentOrder
    let authorizing = true
    let navigating = false
    try {
      const latest = await readPaymentProducts()
      if (!active()) return
      setData(latest)
      const option = latest.paymentOptions.find((item) => item.id === optionId)
      if (
        !latest.providerConfigured ||
        latest.provider !== 'toss' ||
        !option?.available ||
        !option.models.includes(model)
      )
        throw new Error('선택한 결제수단을 현재 사용할 수 없어요.')
      if (
        !latest.products.some(
          (item) =>
            item.id === shownProduct.id &&
            item.active !== false &&
            item.kind !== 'subscription'
        )
      )
        throw new Error('현재 구매할 수 없는 상품이에요.')
      if (!selectedOrder) {
        preparePurchase(ownerId, shownProduct.id, { fresh: !prepared.current })
        prepared.current = true
        selectedOrder = await createPaymentOrder(ownerId, shownProduct.id)
        if (!active()) return
        setCurrentOrder(selectedOrder)
        const before = chargeRewards(shownProduct)
        const after = chargeRewards(selectedOrder.product)
        if (
          selectedOrder.product.amount !== shownProduct.amount ||
          before.credits !== after.credits ||
          before.bonus !== after.bonus ||
          before.firstBonus !== after.firstBonus
        )
          throw new Error(
            '상품 정보가 변경됐어요. 금액과 젬을 확인한 뒤 다시 눌러 주세요.'
          )
      }
      if (
        selectedOrder.provider !== 'toss' ||
        selectedOrder.status !== 'pending'
      )
        throw new Error('현재 주문의 결제 상태를 먼저 확인해 주세요.')
      const sdk = await import('@/features/veluvit/services/toss-sdk')
      if (!active()) return
      const checkout = await startPaymentCheckout(
        ownerId,
        selectedOrder,
        optionId,
        model,
        option.kind === 'brandpay' ? methodId : undefined
      )
      if (!active()) return
      if (
        checkout.paymentOption.kind !== option.kind ||
        (option.kind === 'payment' &&
          (checkout.paymentOption.kind !== 'payment' ||
            checkout.paymentOption.method !== option.method ||
            checkout.paymentOption.easyPay !== option.easyPay))
      )
        throw new Error(
          '선택한 결제수단 정보가 변경됐어요. 다시 선택해 주세요.'
        )
      const redirect = model === 'MOB' && option.kind === 'payment'
      if (redirect) {
        rememberPaymentReturn(ownerId, selectedOrder.id)
        await sdk.openTossCheckout(checkout, active, {
          windowTarget: 'self',
          signal: controller.signal,
          mode: 'redirect',
        })
        navigating = true
        return
      }
      const proof = await runPaymentLayer(controller.signal, (options) =>
        sdk.openTossCheckout(checkout, active, {
          ...options,
          windowTarget: 'iframe',
          mode: 'promise',
        })
      )
      if (!active()) return
      authorizing = false
      authorization.current = proof
      setConfirming(true)
      const confirmed = await confirmPaymentOrder(ownerId, selectedOrder, proof)
      if (!sameOwner()) return
      usePaymentFlowStore
        .getState()
        .watch(ownerId, confirmed.id, layerId.current ?? undefined)
      if (active()) {
        setCurrentOrder(confirmed)
        setConfirming(confirmed.status === 'pending')
        if (confirmed.status !== 'pending') authorization.current = null
      }
    } catch (cause) {
      if (!sameOwner()) return
      if (!authorizing && selectedOrder) {
        usePaymentFlowStore
          .getState()
          .watch(ownerId, selectedOrder.id, layerId.current ?? undefined)
        toast.show('결제 결과를 확인하고 있어요. 확인되면 알려드릴게요.')
      } else if (active()) {
        const cancelled =
          typeof cause === 'object' &&
          cause !== null &&
          'code' in cause &&
          (cause.code === 'USER_CANCEL' ||
            cause.code === 'PAY_PROCESS_CANCELED')
        if (cancelled) toast.show('결제를 취소했어요.')
        else
          toast.error(
            cause instanceof Error ? cause.message : '결제창을 열지 못했어요.'
          )
      }
    } finally {
      if (paymentController.current === controller)
        paymentController.current = null
      locked.current = false
      if (!navigating && mounted.current && sameOwner()) setPending(false)
    }
  }

  if (user?.id !== ownerId || user.guest || !shownProduct) return null
  if (manageCards)
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => setManageCards(false)}>
          결제로 돌아가기
        </Button>
        <BillingMethodsPanel ownerId={ownerId} />
      </div>
    )
  const rewards = chargeRewards(shownProduct)
  const deposit =
    currentOrder?.status === 'awaiting_deposit'
      ? currentOrder.payment.virtualAccount
      : null
  return (
    <section ref={sectionRef} className="text-left">
      <div className="flex items-center gap-4 border-b border-border pb-5">
        {illustrationTier !== undefined && (
          <Image
            src={chargeArt(illustrationTier)}
            alt=""
            width={512}
            height={512}
            sizes="80px"
            className="size-20 flex-none object-contain"
          />
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-c16 font-bold over10:text-c18">
            {shownProduct.name}
          </h2>
          {rewards.credits !== undefined && (
            <p className="mt-1 text-c14 leading-6 text-description">
              기본 {rewards.credits.toLocaleString('ko-KR')}젬
            </p>
          )}
          {rewards.firstBonus > 0 && (
            <p className="text-c14 font-semibold leading-6 text-sub">
              첫 충전 +{rewards.firstBonus.toLocaleString('ko-KR')}젬
            </p>
          )}
          {rewards.bonus > 0 && (
            <p className="text-c13 leading-5 text-description">
              기본 보너스 +{rewards.bonus.toLocaleString('ko-KR')}젬
            </p>
          )}
        </div>
        <strong className="flex-none text-c18 tabular-nums">
          {shownProduct.amount.toLocaleString('ko-KR')}원
        </strong>
      </div>
      {(!currentOrder || currentOrder.status === 'pending') && (
        <form className="mt-5 grid gap-5" onSubmit={submit}>
          <fieldset
            disabled={pending || confirming}
            className="min-w-0 border-0 p-0"
          >
            <legend className="sr-only">등록한 카드 선택</legend>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-c15 font-semibold">등록한 카드</h3>
              <Button variant="ghost" size="sm" onClick={openCards}>
                카드 등록·관리
              </Button>
            </div>
            {registered.length > 0 ? (
              <div className="grid gap-2">
                {registered.map((card) => (
                  <Button
                    key={card.id}
                    variant="choice"
                    shape="field"
                    disabled={
                      !brandpay?.available ||
                      !model ||
                      !brandpay.models.includes(model)
                    }
                    aria-pressed={
                      optionId === 'brandpay' && methodId === card.id
                    }
                    onClick={() => {
                      touched.current = true
                      setOptionId('brandpay')
                      setMethodId(card.id)
                    }}
                  >
                    <LuCreditCard
                      aria-hidden="true"
                      className="size-5 flex-none"
                    />
                    <span className="min-w-0 flex-1 text-left">
                      {card.label}
                    </span>
                    <span className="text-c13 text-description">
                      {card.last4 ? '•• ' + card.last4 : card.brand}
                    </span>
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-c13 leading-6 text-description over10:text-c14">
                한 번 등록한 카드는 다음 결제에 바로 선택할 수 있어요.
              </p>
            )}
          </fieldset>
          <fieldset
            disabled={pending || confirming}
            className="min-w-0 border-0 p-0"
          >
            <legend className="mb-3 text-c15 font-semibold">
              카드 등록 없이 결제
            </legend>
            <div
              className="grid grid-cols-2 gap-2"
              role="group"
              aria-label="결제수단 선택"
            >
              {data?.paymentOptions
                .filter((option) => option.kind === 'payment')
                .map((option) => (
                  <PaymentOptionButton
                    key={option.id}
                    label={option.label}
                    method={
                      option.kind === 'payment' ? option.method : undefined
                    }
                    brand={
                      option.kind === 'payment' ? option.easyPay : undefined
                    }
                    selected={option.id === optionId}
                    disabled={
                      !ready ||
                      !model ||
                      !option.available ||
                      !option.models.includes(model)
                    }
                    onSelect={() => {
                      touched.current = true
                      setOptionId(option.id)
                    }}
                  />
                ))}
            </div>
          </fieldset>
          <Button
            type="submit"
            size="lg"
            loading={pending}
            disabled={!canPay || confirming}
            className="w-full"
          >
            {confirming
              ? '결제 결과 확인 중'
              : shownProduct.amount.toLocaleString('ko-KR') + '원 결제하기'}
          </Button>
        </form>
      )}
      {deposit && (
        <div className="mt-5 space-y-2 text-c14">
          <p className="font-semibold">
            {deposit.bankName} {deposit.number}
          </p>
          <p className="text-description">입금 기한: {deposit.expiresAt}</p>
          <p className="text-description">입금이 확인된 뒤 젬이 지급돼요.</p>
        </div>
      )}
      {currentOrder && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            loading={pending}
            onClick={() => void check()}
          >
            결제 상태 확인
          </Button>
        </div>
      )}
    </section>
  )
}
